/**
 * Qari Onboarding & Discovery — Supabase data access (Phase 1).
 *
 * Thin wrappers around the supabase-js client. Each function maps to a
 * single intent the UI calls into; status transitions are enforced by
 * the trigger in `seed-qari.sql`, not here.
 */

import { supabase } from '@/lib/supabase/client';
import type {
  QariDocument,
  QariDocumentKind,
  QariProfile,
  QariProfileDraft,
} from '@/types/qari';

const QARI_BUCKET = 'qari-documents';

// ---------- profile ----------

/**
 * Returns the caller's own qari_profile row, or null if it has not
 * been created yet.
 *
 * We pass `userId` and filter explicitly even though RLS would scope
 * the result. The `qari_profiles_select_approved` policy overlaps with
 * `qari_profiles_select_own`, so an unfiltered SELECT returns the
 * caller's row PLUS every approved row — which would make `.maybeSingle()`
 * error out the moment there's more than one approved qari in the system.
 */
export async function getMyQariProfile(
  userId: string,
): Promise<QariProfile | null> {
  const { data, error } = await supabase
    .from('qari_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return (data as QariProfile | null) ?? null;
}

/**
 * Upsert the caller's qari_profile row. We pass `user_id` so the
 * insert path works on first save; subsequent saves match on the
 * primary key and update.
 */
export async function upsertQariDraft(
  userId: string,
  patch: Partial<QariProfileDraft>,
): Promise<QariProfile> {
  const { data, error } = await supabase
    .from('qari_profiles')
    .upsert(
      {
        user_id: userId,
        ...patch,
      },
      { onConflict: 'user_id' },
    )
    .select('*')
    .single();
  if (error) throw error;
  return data as QariProfile;
}

/**
 * Move profile from draft (or rejected) to pending_review. The status
 * trigger enforces that the transition is legal for the current user.
 */
export async function submitQariProfileForReview(
  userId: string,
): Promise<QariProfile> {
  const { data, error } = await supabase
    .from('qari_profiles')
    .update({ status: 'pending_review' })
    .eq('user_id', userId)
    .select('*')
    .single();
  if (error) throw error;
  return data as QariProfile;
}

/**
 * Pull a submitted profile back to draft (qari withdrawing their
 * submission, or starting a revision after rejection).
 */
export async function withdrawQariSubmission(
  userId: string,
): Promise<QariProfile> {
  const { data, error } = await supabase
    .from('qari_profiles')
    .update({ status: 'draft' })
    .eq('user_id', userId)
    .select('*')
    .single();
  if (error) throw error;
  return data as QariProfile;
}

// ---------- documents ----------

export async function listMyQariDocuments(
  userId: string,
): Promise<QariDocument[]> {
  const { data, error } = await supabase
    .from('qari_documents')
    .select('*')
    .eq('qari_user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as QariDocument[];
}

/**
 * Upload a file to the qari-documents bucket AND insert the matching
 * metadata row. Returns the metadata row.
 *
 * Path layout: `<user_id>/<kind>-<timestamp>-<safe-name>`
 *  — keeps every user's uploads under their own folder so storage
 *  policies in seed-qari.sql can scope by foldername[1].
 */
export async function uploadQariDocument(input: {
  userId: string;
  kind: QariDocumentKind;
  file: File;
  notes?: string | null;
}): Promise<QariDocument> {
  const { userId, kind, file, notes = null } = input;

  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File too large (max 10 MB).');
  }

  const allowed = new Set([
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
  ]);
  if (!allowed.has(file.type)) {
    throw new Error('Unsupported file type. Upload PDF, JPG, PNG, or WebP.');
  }

  const safeName = file.name
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .slice(0, 80);
  const path = `${userId}/${kind}-${Date.now()}-${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(QARI_BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type,
    });
  if (uploadError) throw uploadError;

  const { data, error } = await supabase
    .from('qari_documents')
    .insert({
      qari_user_id: userId,
      kind,
      storage_path: path,
      original_filename: file.name.slice(0, 200),
      mime_type: file.type,
      size_bytes: file.size,
      notes,
    })
    .select('*')
    .single();

  if (error) {
    // Best-effort cleanup of the orphaned file if metadata insert failed.
    await supabase.storage.from(QARI_BUCKET).remove([path]);
    throw error;
  }
  return data as QariDocument;
}

export async function deleteQariDocument(doc: QariDocument): Promise<void> {
  // Storage policies + RLS both gate this to the owner.
  const { error: storageError } = await supabase.storage
    .from(QARI_BUCKET)
    .remove([doc.storage_path]);
  if (storageError) throw storageError;

  const { error } = await supabase
    .from('qari_documents')
    .delete()
    .eq('id', doc.id);
  if (error) throw error;
}

/**
 * Sign a private storage path so the qari can preview their own
 * uploaded document. 60 s TTL is plenty for an inline preview.
 */
export async function signQariDocumentUrl(
  storagePath: string,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(QARI_BUCKET)
    .createSignedUrl(storagePath, 60);
  if (error) throw error;
  return data.signedUrl;
}
